import { AsyncPipe } from '@angular/common';
import { Component, inject, OnDestroy } from '@angular/core';
import { intersects } from 'radash';
import { ActivatedRoute, Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { Observable, Subject, take, takeUntil, firstValueFrom } from 'rxjs';
import { Group } from '../../../../models/group.model';
import { SchoolMemberType } from '../../../../models/member.school.model';
import { MembershipStatus } from '../../../../models/membership.status';
import { Notice } from '../../../../models/notice.model';
import { InitialsPipe } from '../../../../pipes/initials.pipe';
import { UtilsService } from '../../../../services/utils.service';
import * as noticeActions from '../../../../state/actions/notice.actions';
import { AppState } from '../../../../state/appstate';
import * as groupSelector from '../../../../state/selectors/group.selector';
import * as noticeSelector from '../../../../state/selectors/notice.selector';
import { BtnBackComponent } from '../../../components/buttons/btn-back/btn-back.component';
import { BtnFabComponent } from '../../../components/buttons/btn-fab/btn-fab.component';
import { BtnMoreComponent } from '../../../components/buttons/btn-more/btn-more.component';
import { BtnRouteComponent } from '../../../components/buttons/btn-route/btn-route.component';
import { TimeBeforeComponent } from '../../../components/time/time-before/time-before.component';
import { MomentPipe } from '../../../../pipes/moment.pipe';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { DlgConfirmComponent } from '../../../components/dialogs/dlg-confirm/dlg-confirm.component';
import { DlgViewImageComponent } from '../../../components/dialogs/dlg-view-image/dlg-view-image.component';

@Component({
  selector: 'app-noticeboard',
  standalone: true,
  imports: [
    AsyncPipe,
    BtnBackComponent,
    BtnMoreComponent,
    BtnFabComponent,
    BtnRouteComponent,
    TimeBeforeComponent,
    InitialsPipe,
    MomentPipe,
  ],
  templateUrl: './noticeboard.component.html',
  styleUrl: './noticeboard.component.css',
})
export class NoticeboardComponent implements OnDestroy {
  activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  router: Router = inject(Router);
  store: Store<AppState> = inject(Store<AppState>);
  private dialog: MatDialog = inject(MatDialog);

  private exit$ = new Subject<void>();

  groupId: string | null = null;
  group: Group | null | undefined = null;

  loading$: Observable<boolean> = new Observable();

  notices$: Observable<Notice[] | undefined> = new Observable();

  myRoles: any;

  membershipStatus = MembershipStatus;
  utils = UtilsService;

  constructor() {
    console.log('NoticeboardComponent :: Initializing...');
    this.activatedRoute.paramMap
      .pipe(takeUntil(this.exit$))
      .subscribe((params) => {
        this.groupId = params.get('id');
        console.log('NoticeboardComponent :: paramMap sub emitted groupId:', this.groupId);
        this.loadNotices();
      });
    this.store
      .select(groupSelector.getSelectedGroup)
      .pipe(takeUntil(this.exit$))
      .subscribe((group) => {
        console.log('NoticeboardComponent :: getSelectedGroup selector emitted group:', group);
        if (group) {
          this.group = group;
          this.loadNotices();
        }
      });
    this.store
      .select(groupSelector.getMyRolesInGroup(this.groupId))
      .pipe(takeUntil(this.exit$))
      .subscribe((roles) => {
        console.log('NoticeboardComponent :: getMyRolesInGroup selector emitted roles:', roles);
        this.myRoles = roles;
      });
    this.notices$ = this.store.select(noticeSelector.getNoticesBySelectedGroup);
    this.notices$.pipe(takeUntil(this.exit$)).subscribe(notices => {
      console.log('NoticeboardComponent :: notices$ selector emitted:', notices);
    });
    this.loading$ = this.store
      .select((state) => state.notices)
      .pipe(takeUntil(this.exit$), select(noticeSelector.loading));
    this.loading$.pipe(takeUntil(this.exit$)).subscribe(loading => {
      console.log('NoticeboardComponent :: loading$ selector emitted:', loading);
    });
  }

  loadNotices() {
    console.log('NoticeboardComponent :: loadNotices called', { group: this.group, groupId: this.groupId });
    if (!this.group || !this.groupId || this.group.id !== this.groupId) {
      console.log('NoticeboardComponent :: loadNotices guard failed', {
        groupMatches: this.group?.id === this.groupId,
        groupExists: !!this.group,
        groupIdExists: !!this.groupId
      });
      return;
    }

    this.store.dispatch(
      noticeActions.getNotices({
        groupId: this.groupId!,
        memberType: this.group?.memberType as SchoolMemberType[],
        academicYear: this.group?.academicYear!,
        gradeId:
          this.group?.groupType === 'school' &&
          intersects(this.group?.memberType as SchoolMemberType[], [
            SchoolMemberType.president,
            SchoolMemberType.principal,
          ])
            ? undefined
            : this.group?.gradeId
            ? [this.group.gradeId]
            : undefined,
      })
    );
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  onClick(item: Notice) {
    console.log(item);
  }

  onViewImage(item: Notice) {
    if (!item.imageUrl) return;
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      imageUrl: item.imageUrl,
      title: item.title,
    };
    dialogConfig.maxWidth = '75vw';
    dialogConfig.maxHeight = '75vh';
    dialogConfig.panelClass = 'image-viewer-dialog-panel';
    this.dialog.open(DlgViewImageComponent, dialogConfig);
  }

  canCreateNotice(): boolean {
    if (this.group?.memberStatus !== MembershipStatus.active) return false;
    if (this.group?.groupType !== 'school') {
      return this.myRoles?.admin === true;
    }
    return this.myRoles?.management === true || (this.myRoles?.faculty && this.group?.gradeId);
  }

  canEditDeleteNotice(item: Notice): boolean {
    if (this.group?.memberStatus !== MembershipStatus.active) return false;
    if (this.group?.groupType !== 'school') {
      return this.myRoles?.admin === true;
    }
    return (this.myRoles?.management || (this.myRoles?.faculty && item.gradeId === this.group?.gradeId)) &&
           this.group?.academicYear === item.academicYear;
  }

  onEdit(item: Notice) {
    this.router.navigate([
      this.group?.groupType === 'school' ? 'school' : 'groups',
      item.groupId,
      'noticeboard',
      'edit',
      item.id,
    ]);
  }

  async dialogConfirm(title: string, description: string): Promise<boolean> {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      title,
      description,
      action: 'Delete',
      cancelText: 'Cancel',
      showType: 'warning',
    };
    const dialogRef = this.dialog.open(DlgConfirmComponent, dialogConfig);
    return firstValueFrom(dialogRef.afterClosed());
  }

  async onDelete(item: Notice) {
    const confirmed = await this.dialogConfirm(
      'Delete Notice',
      `Are you sure you want to delete "${item.title}"? This will delete the notice and its attachments.`
    );
    if (!confirmed) return;

    this.store.dispatch(
      noticeActions.deleteAction({
        groupId: this.groupId!,
        id: item.id,
      }),
    );
  }

  addMore() {
    this.notices$.pipe(take(1)).subscribe((notices) => {
      this.store.dispatch(
        noticeActions.getNotices({
          groupId: this.groupId!,
          memberType: this.group?.memberType as SchoolMemberType[],
          academicYear: this.group?.academicYear!,
          gradeId:
            this.group?.groupType === 'school' &&
            intersects(this.group?.memberType as SchoolMemberType[], [
              SchoolMemberType.president,
              SchoolMemberType.principal,
            ])
              ? undefined
              : this.group?.gradeId
              ? [this.group.gradeId]
              : undefined,
          lastRecord:
            (notices ?? []).length > 0
              ? notices![notices!.length - 1]
              : undefined,
        }),
      );
    });
  }

  /**
   * This is temporary function to assign users to grade notification groups.
   */
  // async assignToGradeNotificationGroups() {
  //   try {
  //     const functions = getFunctions();
  //     const callable = httpsCallable(
  //       functions,
  //       'assignGradeNotificationGroups'
  //     );
  //     const result = await callable({ groupId: this.groupId });
  //     // console.log(`Response: ${JSON.stringify(result)}`);
  //     return result.data;
  //   } catch (error) {
  //     console.log(
  //       `NoticeService :: assignToGradeNotificationGroups: Error: ${error}`
  //     );
  //     return Promise.reject('Unable to fetch Notice Statistics');
  //   }
  // }

  hasEvents(events: any): boolean {
    return events && Object.keys(events).length > 0;
  }

  getSortedEvents(events: any): any[] {
    if (!events) return [];
    return Object.values(events).sort((a: any, b: any) => {
      const timeA = a.date?.toDate ? a.date.toDate().getTime() : 0;
      const timeB = b.date?.toDate ? b.date.toDate().getTime() : 0;
      return timeA - timeB;
    });
  }
}
